import {useAppSelector, useAppDispatch} from '@/store/hooks'
import {selectIsWorkspacePro, selectWorkspace} from '@/store/workspace/workspaceSelectors'
import {selectIsLoading} from '@/store/ui/uiSelectors'
import {TextH2, TextMidd, TextNorm, TextNormBold, TextSmall} from '@/components/ui/Text'
import Button from '@/components/ui/Button/Button'
import WorkspaceSettingsTitle from '../WorkspaceSettingsTitle/WorkspaceSettingsTitle.tsx'
import CheckIcon from '@/assets/icons/icon-check.svg?react'
import {getBillingUrlSaga} from '@/store/workspace/workspaceSlice'
import {AnalyticsEvent, trackEvent} from '@/lib/analytics'
import css from './WorkspaceSettingsModalBilling.module.css'

const PRO_FEATURES = [
    'Unlimited projects',
    'Unlimited members',
    'Unlimited tasks',
    '100GB storage',
    '100MB per file',
]

const FREE_FEATURES = [
    'Free for everyone',
    'Unlimited members',
    '3 projects',
    '300 tasks',
    '1GB storage',
    '25MB per file',
]

const WorkspaceSettingsModalBilling = () => {
    const dispatch = useAppDispatch()
    const isProPlan = useAppSelector(selectIsWorkspacePro)
    const workspace = useAppSelector(selectWorkspace)
    const isBillingLoading = useAppSelector(selectIsLoading('get-billing-url'))

    const activeMembers = workspace.activeMembers ?? 0
    const renewalAmount = activeMembers * 8

    const formatRenewalDate = (timestamp: any): string => {
        if (!timestamp) return ''
        const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp)
        return date.toLocaleDateString('en-US', {month: 'short', day: 'numeric'})
    }

    const renewalDate = formatRenewalDate(workspace.paidThroughDate)

    const handleBillingClick = () => {
        // Same button opens the billing portal on Pro — only a Free-plan click is an
        // upgrade intent. Tracked before the dispatch so the redirect can't drop it.
        if (!isProPlan) {
            trackEvent(AnalyticsEvent.UpgradeClicked, {source: 'billing_tab', workspace_id: workspace?.id})
        }
        dispatch(getBillingUrlSaga())
    }

    return (
        <div>
            <WorkspaceSettingsTitle>Subscription</WorkspaceSettingsTitle>
            <div className={css.plans}>
                <div className={`${css.plan} ${css.planPro}`}>
                    <div className={css.planHeader}>
                        <div className={css.planPrice}>
                            <TextNormBold>Pro</TextNormBold>
                            <TextH2>$8</TextH2>
                        </div>
                        <Button
                            title={isProPlan ? 'Billing settings' : 'Upgrade'}
                            type={isProPlan ? 'white' : 'black'}
                            loading={isBillingLoading}
                            onClick={handleBillingClick}
                        />
                    </div>
                    <TextNorm className={css.planMeta}>Per member, billed monthly</TextNorm>
                    {isProPlan ? <div className={css.active_plan_info}>
                        <TextSmall>Active members: {activeMembers}</TextSmall>
                        <TextSmall>Next renewal: ${renewalAmount}{renewalDate ? ` on ${renewalDate}` : ''}</TextSmall>
                    </div> : <div className={css.divider}/>}
                    <div className={css.features}>
                        {PRO_FEATURES.map((feature) => (
                            <div key={feature} className={css.feature}>
                                <span className={css.featureIcon}><CheckIcon/></span>
                                <TextMidd>{feature}</TextMidd>
                            </div>
                        ))}
                    </div>
                </div>

                {!isProPlan && ( <div className={`${css.plan} ${css.planFree}`}>
                    <div className={css.planHeader}>
                        <div className={css.planPrice}>
                            <TextNormBold>Free</TextNormBold>
                            <TextH2>$0</TextH2>
                        </div>
                        {!isProPlan && <Button title="Current plan" type="white" disabled={true}/>}
                    </div>
                    <TextNorm className={css.planMeta}>Free for everyone</TextNorm>
                    <div className={css.divider}/>
                    <div className={css.features}>
                        {FREE_FEATURES.map((feature) => (
                            <div key={feature} className={css.feature}>
                                <span className={css.featureIcon}><CheckIcon/></span>
                                <TextMidd>{feature}</TextMidd>
                            </div>
                        ))}
                    </div>
                </div>)}


            </div>
        </div>
    )
}

export default WorkspaceSettingsModalBilling
